/**
 * Reconciliation and the month-end close.
 *
 * **Nothing on this screen is derived from the rows in hand.** Whether a day is clean, how old a
 * break is, and how many days is too many are all the server's answers — it needs every pass to
 * have *run* as well as every break to be closed, and a console re-deriving that would call a day
 * clean because the pass that never reported is also the pass with no row.
 *
 * **A computed checklist item is shown with no action at all, not a disabled one.** A greyed-out
 * button says the capability exists and you lack it; C1, C2, C4 and C6 are evaluated from the
 * ledger and cannot be ticked by anybody, so the screen says what the server observed instead.
 *
 * **The four-eyes rules stay on the server.** Sign-off asks for a second admin's id and shows the
 * refusal verbatim; the screen keeps no copy of "who may second whom", because a client copy of a
 * server rule has been wrong three times in this codebase.
 */
import { useMemo, useState } from 'react';
import { Chip, Icon, StatusChip, type Tone } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { newIdempotencyKey } from '../lib/api/idempotency';
import { GatewayFees } from './GatewayFees';
import {
  useAcceptBreak,
  useBreakAging,
  useCheckItem,
  useMonthClose,
  useReconBreaks,
  useReconDay,
  useResolveBreak,
  useRunReconDay,
  useSignClose,
  useWaiveItem,
  type ChecklistItemRow,
  type ReconRunRow,
} from '../lib/api/hooks/recon';

/** The last six months, newest first. Derived rather than listed, so it never goes stale. */
export function recentPeriods(today: Date, count = 6): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** Yesterday in IST, which is the day the nightly job reconciles. */
export function reconciledDay(now: Date): string {
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000);
  ist.setUTCDate(ist.getUTCDate() - 1);
  return ist.toISOString().slice(0, 10);
}

/* Keyed by the enum, with no fallback: `MonthCloseItemStatus` is closed and all four are here,
   so a `?? 'neutral'` would be a branch nothing can reach. */
const ITEM_TONE: Record<ChecklistItemRow['status'], Tone> = {
  passing: 'success',
  failing: 'error',
  waived: 'warn',
  pending: 'neutral',
};

/* Same: `ReconRunStatus` is closed and all four are here. */
const RUN_TONE: Record<ReconRunRow['status'], Tone> = {
  clean: 'success',
  broken: 'error',
  failed: 'warn',
  running: 'info',
};

export function MonthClose() {
  const { scope, toast } = useConsole();
  const periods = useMemo(() => recentPeriods(new Date()), []);
  const [period, setPeriod] = useState(periods[0]!);
  const day = useMemo(() => reconciledDay(new Date()), []);

  const close = useMonthClose(period);
  const dayRuns = useReconDay(day);
  const aging = useBreakAging();
  const breaks = useReconBreaks({ page: 1, page_size: 50, break_status: 'open' });

  const checkItem = useCheckItem(period);
  const waiveItem = useWaiveItem(period);
  const signClose = useSignClose(period);
  const resolveBreak = useResolveBreak();
  const acceptBreak = useAcceptBreak();
  const runDay = useRunReconDay(day);

  const [pending, setPending] = useState<{ item: ChecklistItemRow; mode: 'check' | 'waive' } | null>(
    null,
  );
  const [signing, setSigning] = useState<string | null>(null);
  const [founderId, setFounderId] = useState('');
  const [accepting, setAccepting] = useState<{ id: number; label: string } | null>(null);
  const [seconder, setSeconder] = useState('');
  const [rerunning, setRerunning] = useState<string | null>(null);

  /* **One gate for the whole report, not one per panel.** A month-end close is a single picture:
     showing three-quarters of it while the fourth is still arriving invites somebody to read the
     checklist as complete when the break queue has not answered yet. Errors stay per-panel,
     because one read failing is a fact about that read. */
  if (close.isLoading || dayRuns.isLoading || breaks.isLoading) {
    return <LoadingState message="Reading the month-end close" />;
  }
  if (close.isError) return <ErrorState title="The checklist could not be read" cause={errorMessage(close.error)} />;

  const detail = close.data!;
  const outstanding = detail.items.filter(
    (i) => i.status !== 'passing' && i.status !== 'waived',
  );
  const closed = detail.status === 'closed';

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: closed ? 'var(--c-success-bg)' : 'var(--c-surface2)',
          color: closed ? 'var(--c-success-text)' : 'var(--c-ink2)',
          border: `1px solid ${closed ? 'var(--c-success)' : 'var(--c-border)'}`,
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name={closed ? 'lock' : 'clock'} size={16} />
        <span style={{ flex: 1, minWidth: 260 }}>
          {closed
            ? `${period} is closed and frozen. Every later correction posts into the open month instead.`
            : `${period} is ${detail.status}. ${outstanding.length} checklist item(s) outstanding, ${detail.open_break_count} break(s) open.`}
        </span>
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          disabled={closed}
          onClick={() => {
            setFounderId('');
            setSigning(newIdempotencyKey());
          }}
        >
          Sign off the month
        </ScopeAction>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
        {periods.map((p) => (
          <Chip
            key={p}
            variant="filter"
            label={p}
            selected={p === period}
            onClick={() => setPeriod(p)}
          />
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: 'var(--sp-lg)',
          alignItems: 'start',
        }}
      >
        <Section
          title="Month-end checklist"
          note={`${period} · C1, C2, C4 and C6 are read from the ledger and cannot be ticked by hand`}
          pad={false}
        >
          <DataTable
            density="compact"
            columns={[
              { key: 'code', label: 'Item' },
              { key: 'state', label: 'State' },
              { key: 'evidence', label: 'What the server saw' },
              { key: 'action', label: '', align: 'right' },
            ]}
            rows={detail.items.map((item) => ({
              id: item.id,
              code: (
                <span style={{ display: 'grid', gap: 2 }}>
                  <span>{item.title ? `${item.item_code} · ${item.title}` : item.item_code}</span>
                  {item.detail && (
                    <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                      {item.detail}
                    </span>
                  )}
                </span>
              ),
              state: <StatusChip tone={ITEM_TONE[item.status]} label={item.status} />,
              evidence: item.evidence ?? '—',
              action: item.is_computed ? (
                /* No button at all — a disabled one would claim the capability exists. */
                <span style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                  computed
                </span>
              ) : (
                <span style={{ display: 'inline-flex', gap: 'var(--sp-xs)' }}>
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="secondary"
                    disabled={closed}
                    onClick={() => setPending({ item, mode: 'check' })}
                  >
                    Tick
                  </ScopeAction>
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    variant="ghost"
                    disabled={closed}
                    onClick={() => setPending({ item, mode: 'waive' })}
                  >
                    Waive
                  </ScopeAction>
                </span>
              ),
            }))}
          />
        </Section>

        <div style={{ display: 'grid', gap: 'var(--sp-lg)', minWidth: 0 }}>
          <Section
            title={`Last night's passes · ${day}`}
            note="A day is clean only when every pass RAN and every break is closed"
            actions={
              <ScopeAction scope={scope} need="finance" size="sm" onClick={() => setRerunning(newIdempotencyKey())}>
                Run again
              </ScopeAction>
            }
            pad={false}
          >
            {dayRuns.isError ? (
              <ErrorState title="The day could not be read" cause={errorMessage(dayRuns.error)} />
            ) : dayRuns.data!.runs.length === 0 ? (
              <EmptyState
                title="No pass has reported for this day"
                body="The nightly job runs at 03:00 IST. A day with no runs is not a clean day."
              />
            ) : (
              <DataTable
                density="compact"
                columns={[
                  { key: 'pass', label: 'Pass' },
                  { key: 'state', label: 'State' },
                  { key: 'matched', label: 'Matched', align: 'right', mono: true },
                  { key: 'why', label: 'Why not' },
                ]}
                rows={dayRuns.data!.runs.map((run) => ({
                  id: run.id,
                  pass: run.pass_code,
                  state: <StatusChip tone={RUN_TONE[run.status]} label={run.status} />,
                  matched: run.matched_count,
                  why: run.failure_reason ?? '—',
                }))}
              />
            )}
          </Section>

          <Section
            title="Open breaks"
            note={
              aging.data
                ? `Re-alerts after ${aging.data.alert_days} days · the founder is told after ${aging.data.founder_days}`
                : /* The thresholds could not be read. Saying "oldest first" is true whatever
                     happened; printing a default like "2 days" would be this screen inventing a
                     dial only the server owns. */
                  'Oldest difference first'
            }
            pad={false}
          >
            {breaks.isError ? (
              <ErrorState title="The break queue could not be read" cause={errorMessage(breaks.error)} />
            ) : breaks.data!.items.length === 0 ? (
              <EmptyState
                title="Nothing unexplained"
                body="Every difference the passes found has been resolved or accepted."
              />
            ) : (
              <DataTable
                density="compact"
                columns={[
                  { key: 'kind', label: 'Break' },
                  { key: 'ref', label: 'Reference' },
                  { key: 'expected', label: 'Ours', align: 'right', mono: true },
                  { key: 'actual', label: 'Theirs', align: 'right', mono: true },
                  { key: 'action', label: '', align: 'right' },
                ]}
                rows={breaks.data!.items.map((row) => ({
                  id: row.id,
                  kind: row.break_class,
                  ref: row.provider_ref ?? row.internal_ref ?? '—',
                  /* NULL is not zero: zero says we expected nothing and got something, NULL says
                     no row exists on our side at all — which for `unknown_payment` IS the
                     finding. */
                  expected:
                    row.expected_paise === null ? (
                      <span style={{ color: 'var(--c-ink3)' }}>no row</span>
                    ) : (
                      <Money paise={row.expected_paise} showPaise />
                    ),
                  actual:
                    row.actual_paise === null ? (
                      <span style={{ color: 'var(--c-ink3)' }}>—</span>
                    ) : (
                      <Money paise={row.actual_paise} showPaise />
                    ),
                  action: (
                    <span style={{ display: 'inline-flex', gap: 'var(--sp-xs)' }}>
                      <ScopeAction
                        scope={scope}
                        need="finance"
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                        resolveBreak.mutate(
                          {
                            breakId: row.id,
                            kind: 'timing',
                            note: 'resolved from the close screen',
                            key: newIdempotencyKey(),
                          },
                          {
                            onSuccess: () =>
                              toast({ tone: 'success', message: 'Break resolved' }),
                            onError: (error) =>
                              toast({ tone: 'error', message: errorMessage(error) }),
                          },
                        )
                      }
                      >
                        Resolve
                      </ScopeAction>
                      <ScopeAction
                        scope={scope}
                        need="finance"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSeconder('');
                          setAccepting({ id: row.id, label: row.break_class });
                        }}
                      >
                        Accept
                      </ScopeAction>
                    </span>
                  ),
                }))}
              />
            )}
          </Section>
        </div>
      </div>

      <div style={{ marginTop: 'var(--sp-lg)' }}>
        <GatewayFees />
      </div>

      <ConfirmAction
        open={pending !== null}
        title={
          pending?.mode === 'waive'
            ? `Waive ${pending.item.item_code}?`
            : `Tick ${pending?.item.item_code}?`
        }
        phrase={pending?.mode === 'waive' ? 'WAIVE' : 'TICK'}
        tone="warn"
        reason
        consequences={
          pending?.mode === 'waive'
            ? [
                'A waiver is your decision, recorded against your name on a document two people sign.',
                'It stands through every later re-evaluation — nothing will quietly undo it.',
              ]
            : ['Your name goes against this item as the person who verified it.']
        }
        onCancel={() => setPending(null)}
        onConfirm={(reason) => {
          const item = pending!.item;
          const mode = pending!.mode;
          setPending(null);
          const key = newIdempotencyKey();
          const handlers = {
            onSuccess: () => toast({ tone: 'success', message: `${item.item_code} updated` }),
            onError: (error: unknown) =>
              toast({ tone: 'error', message: errorMessage(error) }),
          };
          if (mode === 'waive') {
            waiveItem.mutate({ code: item.item_code, reason, key }, handlers);
          } else {
            checkItem.mutate({ code: item.item_code, evidence: reason, key }, handlers);
          }
        }}
        confirmLabel={pending?.mode === 'waive' ? 'Waive it' : 'Tick it'}
      />

      <ConfirmAction
        open={signing !== null}
        title={`Sign off and freeze ${period}?`}
        phrase={`CLOSE ${period}`}
        tone="error"
        consequences={[
          `Nothing can post to ${period} afterwards — every later correction goes into the open month as a compensating entry.`,
          'It needs a second admin, and the server refuses the two being the same person.',
          `${outstanding.length} checklist item(s) are still outstanding; the server refuses an incomplete checklist.`,
        ]}
        onCancel={() => setSigning(null)}
        onConfirm={() => {
          const key = signing!;
          setSigning(null);
          signClose.mutate(
            { founder_admin_id: Number(founderId), key },
            {
              onSuccess: () => toast({ tone: 'success', message: `${period} closed and frozen` }),
              /* The server's words, not ours: it owns who may second whom, and a client copy of
                 that rule has been wrong three times in this codebase. */
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
        confirmLabel="Freeze the month"
      >
        <Input
          label="Second admin's id"
          helper="The person seconding this close. It may not be you — the server checks."
          value={founderId}
          onChange={setFounderId}
        />
      </ConfirmAction>

      <ConfirmAction
        open={rerunning !== null}
        title={`Run every pass for ${day} again?`}
        phrase={`RUN ${day}`}
        tone="warn"
        reason
        consequences={[
          `Every pass for ${day} runs again, the way the nightly job does, and replaces what it last said.`,
          'It cannot make a day clean on its own: a pass with no source still records failed.',
        ]}
        onCancel={() => setRerunning(null)}
        onConfirm={(reason) => {
          const key = rerunning!;
          setRerunning(null);
          runDay.mutate(
            { reason, key },
            {
              onSuccess: () => toast({ tone: 'success', message: `${day} re-run` }),
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
        confirmLabel="Run the passes"
      />

      <ConfirmAction
        open={accepting !== null}
        title={`Accept ${accepting?.label} permanently?`}
        phrase="ACCEPT"
        tone="error"
        reason="Why this difference is genuine and will not be explained"
        consequences={[
          'Nothing has changed to explain this one — accepting closes it as a permanent, known difference.',
          'It needs a second admin, and the server refuses the two being the same person.',
          'The break stops counting against the day and against the month-end checklist.',
        ]}
        onCancel={() => setAccepting(null)}
        onConfirm={(reason) => {
          const target = accepting!;
          setAccepting(null);
          acceptBreak.mutate(
            {
              breakId: target.id,
              seconded_by_admin_id: Number(seconder),
              note: reason,
              key: newIdempotencyKey(),
            },
            {
              onSuccess: () => toast({ tone: 'success', message: 'Difference accepted' }),
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
        confirmLabel="Accept it"
      >
        <Input
          label="Second admin's id"
          helper="The person agreeing this difference is permanent. It may not be you."
          value={seconder}
          onChange={setSeconder}
        />
      </ConfirmAction>
    </>
  );
}
